import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, Plus, Trash2, AlertTriangle, Sparkles, ListChecks } from "lucide-react";
import { DatePicker } from "@/components/ui/date-picker";
import {
  useEvents, useChecklistTasks, useChecklistProgress, useEventStaff,
  useUpdateTask, useCreateTask, useDeleteTask, useGenerateFromTemplate, useTemplates,
  CATEGORIES, CATEGORY_COLORS, CATEGORY_PROGRESS_COLORS, PRIORITY_COLORS, STATUS_COLORS, formatCategory
} from "@/hooks/useChecklistsData";

const EventChecklistTab: React.FC = () => {
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [expandedCats, setExpandedCats] = useState<Set<string>>(new Set(CATEGORIES));
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterPriority, setFilterPriority] = useState<string>("all");
  const [filterAssigned, setFilterAssigned] = useState<string>("all");
  const [addTaskCat, setAddTaskCat] = useState<string | null>(null);
  const [newTask, setNewTask] = useState({ task: "", priority: "medium", notes: "" });
  const [templateModalOpen, setTemplateModalOpen] = useState(false);

  const { data: events = [] } = useEvents();
  const { data: tasks = [], isLoading } = useChecklistTasks(selectedEventId);
  const { data: progress = [] } = useChecklistProgress(selectedEventId);
  const { data: staff = [] } = useEventStaff();
  const { data: templates = [] } = useTemplates();

  const updateTask = useUpdateTask();
  const createTask = useCreateTask();
  const deleteTask = useDeleteTask();
  const generateFromTemplate = useGenerateFromTemplate();

  const today = new Date().toISOString().split("T")[0];

  // Overall progress
  const overallProgress = useMemo(() => {
    const total = progress.reduce((s: number, p: any) => s + (p.total_tasks || 0), 0);
    const done = progress.reduce((s: number, p: any) => s + (p.completed || 0), 0);
    return total > 0 ? Math.round((done / total) * 100) : 0;
  }, [progress]);

  const totalTasks = progress.reduce((s: number, p: any) => s + (p.total_tasks || 0), 0);
  const totalDone = progress.reduce((s: number, p: any) => s + (p.completed || 0), 0);
  const totalUrgent = progress.reduce((s: number, p: any) => s + (p.urgent_open || 0), 0);
  const totalBlocked = progress.reduce((s: number, p: any) => s + (p.blocked || 0), 0);

  // Group tasks by category
  const groupedTasks = useMemo(() => {
    let filtered = tasks;
    if (filterStatus !== "all") filtered = filtered.filter((t: any) => t.status === filterStatus);
    if (filterPriority !== "all") filtered = filtered.filter((t: any) => t.priority === filterPriority);
    if (filterAssigned !== "all") filtered = filtered.filter((t: any) => t.assigned_to === filterAssigned);

    const groups: Record<string, any[]> = {};
    CATEGORIES.forEach(c => { groups[c] = []; });
    filtered.forEach((t: any) => {
      const cat = t.category || "other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(t);
    });
    return groups;
  }, [tasks, filterStatus, filterPriority, filterAssigned]);

  const toggleCat = (cat: string) => {
    const next = new Set(expandedCats);
    next.has(cat) ? next.delete(cat) : next.add(cat);
    setExpandedCats(next);
  };

  const handleCheckbox = (task: any) => {
    const isDone = task.status === "done";
    updateTask.mutate({
      id: task.id,
      updates: {
        status: isDone ? "todo" : "done",
        completed_at: isDone ? null : new Date().toISOString(),
        completed_by: isDone ? null : task.assigned_to,
      },
    });
  };

  const handleAddTask = () => {
    if (!addTaskCat || !selectedEventId || !newTask.task.trim()) return;
    createTask.mutate({
      event_id: selectedEventId,
      category: addTaskCat,
      task: newTask.task,
      priority: newTask.priority,
      status: "todo",
      notes: newTask.notes || null,
    });
    setAddTaskCat(null);
    setNewTask({ task: "", priority: "medium", notes: "" });
  };

  const handleGenerate = (templateId: string) => {
    if (!selectedEventId) return;
    generateFromTemplate.mutate({ eventId: selectedEventId, templateId });
    setTemplateModalOpen(false);
  };

  const getStaffName = (id: string | null) => {
    if (!id) return "—";
    return staff.find((s: any) => s.id === id)?.name || "—";
  };

  const getCatProgress = (cat: string) => {
    const p = progress.find((pr: any) => pr.category === cat);
    return p || { total_tasks: 0, completed: 0, pct_complete: 0, urgent_open: 0 };
  };

  return (
    <div className="space-y-4">
      {/* Event selector */}
      <Select value={selectedEventId || ""} onValueChange={v => setSelectedEventId(v)}>
        <SelectTrigger className="w-full max-w-md">
          <SelectValue placeholder="Select an event..." />
        </SelectTrigger>
        <SelectContent>
          {events.map((e: any) => (
            <SelectItem key={e.id} value={e.id}>{e.name} ({e.start_date})</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!selectedEventId && (
        <Card><CardContent className="py-8 text-center text-muted-foreground">Select an event to view its checklist.</CardContent></Card>
      )}

      {selectedEventId && !isLoading && tasks.length === 0 && (
        <Card className="border-dashed border-2">
          <CardContent className="py-12 text-center space-y-4">
            <ListChecks className="h-12 w-12 mx-auto text-muted-foreground" />
            <h3 className="text-lg font-semibold">No checklist for this event yet</h3>
            <p className="text-muted-foreground">Generate tasks from a template to get started quickly.</p>
            <Button onClick={() => setTemplateModalOpen(true)} className="gap-2">
              <Sparkles className="h-4 w-4" /> Generate from Template
            </Button>
          </CardContent>
        </Card>
      )}

      {selectedEventId && tasks.length > 0 && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card><CardContent className="py-3 text-center">
              <p className="text-xs text-muted-foreground">Overall</p>
              <p className="text-xl font-bold">{overallProgress}%</p>
              <Progress value={overallProgress} className="h-2 mt-1" />
            </CardContent></Card>
            <Card><CardContent className="py-3 text-center">
              <p className="text-xs text-muted-foreground">Total Tasks</p>
              <p className="text-xl font-bold">{totalDone}/{totalTasks}</p>
            </CardContent></Card>
            <Card><CardContent className="py-3 text-center">
              <p className="text-xs text-muted-foreground">Urgent Open</p>
              <p className={`text-xl font-bold ${totalUrgent > 0 ? "text-red-600" : ""}`}>{totalUrgent}</p>
            </CardContent></Card>
            <Card><CardContent className="py-3 text-center">
              <p className="text-xs text-muted-foreground">Blocked</p>
              <p className={`text-xl font-bold ${totalBlocked > 0 ? "text-red-600" : ""}`}>{totalBlocked}</p>
            </CardContent></Card>
            <Card><CardContent className="py-3 text-center">
              <p className="text-xs text-muted-foreground">Generate More</p>
              <Button size="sm" variant="outline" onClick={() => setTemplateModalOpen(true)} className="mt-1 gap-1">
                <Sparkles className="h-3 w-3" /> Template
              </Button>
            </CardContent></Card>
          </div>

          {/* Category progress cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {CATEGORIES.map(cat => {
              const p = getCatProgress(cat);
              if (p.total_tasks === 0) return null;
              return (
                <Card key={cat} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => {
                  const next = new Set<string>([cat]);
                  setExpandedCats(next);
                }}>
                  <CardContent className="py-2 px-3">
                    <div className="flex items-center justify-between">
                      <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[cat]}`}>{formatCategory(cat)}</Badge>
                      {p.urgent_open > 0 && <Badge variant="destructive" className="text-xs">{p.urgent_open}</Badge>}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Progress value={p.pct_complete || 0} className={`h-1.5 flex-1`} />
                      <span className="text-xs font-medium">{p.completed}/{p.total_tasks}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="todo">To Do</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="done">Done</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="w-36"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterAssigned} onValueChange={setFilterAssigned}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Assigned To" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff</SelectItem>
                {staff.map((s: any) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Task list grouped by category */}
          <div className="space-y-2">
            {CATEGORIES.map(cat => {
              const catTasks = groupedTasks[cat] || [];
              if (catTasks.length === 0 && filterStatus === "all" && filterPriority === "all") return null;
              if (catTasks.length === 0) return null;
              const p = getCatProgress(cat);
              const isOpen = expandedCats.has(cat);

              return (
                <Collapsible key={cat} open={isOpen} onOpenChange={() => toggleCat(cat)}>
                  <CollapsibleTrigger className="w-full">
                    <Card className="hover:shadow-sm transition-shadow">
                      <CardContent className="py-2 px-4 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          <Badge variant="outline" className={CATEGORY_COLORS[cat]}>{formatCategory(cat)}</Badge>
                          <span className="text-sm text-muted-foreground">{p.completed}/{p.total_tasks} done</span>
                        </div>
                        <div className="flex items-center gap-2">
                          {p.urgent_open > 0 && <Badge variant="destructive" className="text-xs">{p.urgent_open} urgent</Badge>}
                          <Progress value={p.pct_complete || 0} className="h-2 w-20" />
                        </div>
                      </CardContent>
                    </Card>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="ml-2 border-l-2 border-muted pl-3 mt-1 space-y-1">
                      {catTasks.map((task: any) => {
                        const isOverdue = task.due_date && task.due_date < today && task.status !== "done";
                        return (
                          <div key={task.id} className={`flex items-start gap-2 p-2 rounded-md border ${isOverdue ? "border-red-300 bg-red-50" : "border-border bg-card"}`}>
                            <Checkbox
                              checked={task.status === "done"}
                              onCheckedChange={() => handleCheckbox(task)}
                              className="mt-1"
                            />
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant="outline" className={`text-xs ${PRIORITY_COLORS[task.priority]}`}>{task.priority}</Badge>
                                <span className={`text-sm ${task.status === "done" ? "line-through text-muted-foreground" : ""}`}>{task.task}</span>
                                {isOverdue && <Badge variant="destructive" className="text-xs gap-1"><AlertTriangle className="h-3 w-3" />Overdue</Badge>}
                              </div>
                              <div className="flex items-center gap-2 flex-wrap text-xs">
                                <Select value={task.status} onValueChange={v => updateTask.mutate({ id: task.id, updates: { status: v, ...(v === "done" ? { completed_at: new Date().toISOString() } : { completed_at: null }) } })}>
                                  <SelectTrigger className="h-6 w-28 text-xs"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="todo">To Do</SelectItem>
                                    <SelectItem value="in_progress">In Progress</SelectItem>
                                    <SelectItem value="blocked">Blocked</SelectItem>
                                    <SelectItem value="done">Done</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Select value={task.assigned_to || "unassigned"} onValueChange={v => updateTask.mutate({ id: task.id, updates: { assigned_to: v === "unassigned" ? null : v } })}>
                                  <SelectTrigger className="h-6 w-32 text-xs"><SelectValue placeholder="Assign" /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="unassigned">Unassigned</SelectItem>
                                    {staff.map((s: any) => (
                                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <DatePicker
                                  date={task.due_date ? new Date(task.due_date + "T00:00:00") : undefined}
                                  setDate={d => updateTask.mutate({ id: task.id, updates: { due_date: d ? d.toISOString().split("T")[0] : null } })}
                                  placeholder="Due date"
                                  className="h-6 w-36 text-xs"
                                />
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => deleteTask.mutate(task.id)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                              {task.notes && <p className="text-xs text-muted-foreground">{task.notes}</p>}
                            </div>
                          </div>
                        );
                      })}
                      <Button size="sm" variant="ghost" className="text-xs gap-1" onClick={() => { setAddTaskCat(cat); setNewTask({ task: "", priority: "medium", notes: "" }); }}>
                        <Plus className="h-3 w-3" /> Add Task
                      </Button>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        </>
      )}

      {/* Add task modal */}
      <Dialog open={!!addTaskCat} onOpenChange={() => setAddTaskCat(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task — {addTaskCat ? formatCategory(addTaskCat) : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Task *</Label>
              <Input value={newTask.task} onChange={e => setNewTask(p => ({ ...p, task: e.target.value }))} placeholder="Task description" />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={newTask.priority} onValueChange={v => setNewTask(p => ({ ...p, priority: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={newTask.notes} onChange={e => setNewTask(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTaskCat(null)}>Cancel</Button>
            <Button onClick={handleAddTask} disabled={!newTask.task.trim()}>Add Task</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template selector modal */}
      <Dialog open={templateModalOpen} onOpenChange={setTemplateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Checklist from Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {templates.length === 0 ? (
              <p className="text-muted-foreground text-sm">No templates available. Create one in the Templates tab.</p>
            ) : (
              templates.map((t: any) => (
                <Card key={t.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => handleGenerate(t.id)}>
                  <CardContent className="py-3 flex items-center justify-between">
                    <div>
                      <p className="font-medium">{t.name}</p>
                      {t.description && <p className="text-xs text-muted-foreground">{t.description}</p>}
                    </div>
                    {t.is_default && <Badge variant="secondary">Default</Badge>}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventChecklistTab;
